import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import type { ApiProfileOut3 } from '@agunity/api-v4';
import { Divider } from '@mui/material';

import IDCard from './IDCard';
import QRCode from '../../Search/QRCode';
import BindingTokenTable from './BindingTokenTable';
import AgButton from '../../../stories/Components/AgButton';
import AgTypography from '../../../stories/Styles/AgTypography';

import { createBindCodes, getBindingToken, idCardsRegister, unbindHard, unbindSoft } from './redux';
import { useToast } from '../../../redux/hooks/toast';
import { useModal } from '../../../redux/hooks/modal';
import { useAppDispatch } from '../../../redux/hooks';
import { addThreeYearsFromToday } from '../../../utils';
import { LOGS_USER_PERMISSION, USER_UNBIND_HARD_PERMISSION, USER_UNBIND_SOFT_PERMISSION } from '../../../Constants';
import * as S from './styled';

const BindCard = ({ 
  isStaff = false,
  details,
  identityId, 
  isBound = false, 
  isIdOnly = false,
  permissions = [],
  callback,
  isDirty = false
}: { 
  isStaff?: boolean;
  details: ApiProfileOut3;
  identityId: number; 
  isBound?: boolean; 
  isIdOnly?: boolean;
  permissions?: number[] 
  callback?: () => void
  isDirty?: boolean
}) => {
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();

  const [isLoading, setLoading] = useState(false);
  const [isLoadBindingToken, setLoadingBindingToken] = useState(false);

  const access = useMemo(() => ({
    hasSoftReset: permissions.includes(USER_UNBIND_SOFT_PERMISSION),
    hasHardReset: permissions.includes(USER_UNBIND_HARD_PERMISSION),
    hasLogsUser: permissions.includes(LOGS_USER_PERMISSION),
  }), [permissions]);

  const isShow = useMemo(() => {
    return access.hasSoftReset || access.hasHardReset;
  }, [access]);

  const profileId = details.profiles?.[0]?.id;

  const handleVerifyPin = useCallback(async (bypassPin?: boolean) => {
    try {
      if(isDirty){
        toast('error', messages({ id: 'FORM_DIRTY' }), { temporary: true });
        return false;
      }

      if(!details?.account?.pin && !bypassPin){
        toast('error', messages({ id: 'MISSING_PIN' }), { temporary: true });
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }, [details?.account?.pin, isDirty, messages, toast]);

  const handleUnbindProcess = useCallback(async (isHard: boolean) => {
    try {
      if (!identityId) {
        toast('error', messages({ id: 'INVALID_IDENTITY_ID' }), { temporary: true });
        return;
      }

      if (isHard) {
        await dispatch(unbindHard(Number(identityId)));
      } else {
        await dispatch(unbindSoft(Number(identityId)));
      }
      closeModal();
      toast('success', messages({ id: 'UNBIND_SUCCESS' }), { temporary: true });
      callback?.();
    } catch (error) {
      console.error(error);
      toast('error', messages({ id: 'UNBIND_ERROR' }), { temporary: true });
    }
  }, [closeModal, dispatch, identityId, messages, toast, callback]);

  const handleUnbindModal = useCallback((isHard: boolean) => {
    if (!(isBound || isIdOnly)) return;
    
    openModal({
      closeIcon: true,
      title: messages({ id: `${isHard ? 'HARD' : 'SOFT'}_RESET` }),
      width: 400,
      bodySX: { height: 200 },
      body: <AgTypography message={messages({ id: `${isHard ? 'HARD' : 'SOFT'}_UNBIND_DESC` })} />,
      actions: [
        { label: messages({ id: 'CANCEL' }), variant: 'outlined', style: { marginRight: '16px' } },
        { label: messages({ id: 'PROCEED ' }), onClick: () => handleUnbindProcess(isHard) }
      ]
    });
  }, [handleUnbindProcess, isBound, isIdOnly, messages, openModal]);

  const handleViewIDCard = useCallback(async () => {
    const valid = await handleVerifyPin(true);
    if (!valid) return;

    closeModal();

    openModal({
      closeIcon: true,
      bodySX: { margin: 0, paddingTop: 2 },
      width: 420,
      body: <IDCard details={details} />,
    });
  }, [closeModal, details, handleVerifyPin, openModal]);

  const handleCreateIDCardProcess = useCallback(async () => {
    try {
      const valid = await handleVerifyPin(true);
      if (!valid) return;

      const expire = addThreeYearsFromToday();
      const payload = {
        expiry: expire,
        profileIds: [profileId!],
      };

      await dispatch(idCardsRegister(payload));
      toast('success', messages({ id: 'CREATE_ID_CARD_SUCCESS' }), { temporary: true });
      await callback?.();
      closeModal();
      handleViewIDCard();
    } catch (error) {
      console.error(error);
      toast('error', messages({ id: 'CREATE_ID_CARD_ERROR' }), { temporary: true });
    }
  }, [profileId, dispatch, toast, messages, callback, closeModal, handleViewIDCard, handleVerifyPin]);

  const handleCreateIDCard = useCallback(async () => {
    const valid = await handleVerifyPin(true);
    if (!valid) return;

    closeModal();
    
    openModal({
      closeIcon: true,
      title: messages({ id: 'CREATE_ID_CARD' }),
      width: 400,
      bodySX: { height: 200 },
      body: <AgTypography message={messages({ id: 'CREATE_ID_CARD_DESC' })} />,
      actions: [
        { 
          label: messages({ id: 'BACK' }), 
          variant: 'outlined', 
          style: { marginRight: '16px' },
          onClick: closeModal 
        },
        { 
          label: messages({ id: 'PROCEED' }), 
          onClick:handleCreateIDCardProcess
        }
      ]
    });
  }, [closeModal, handleCreateIDCardProcess, messages, openModal, handleVerifyPin]);

  const handleShowQRCode = useCallback(async () => {
    const valid = await handleVerifyPin();
    if (!valid) return;

    try {
      setLoading(true);
      const response = await dispatch(getBindingToken(identityId));
      const bindingToken = response.data;
      
    openModal({
      closeIcon: true,
      onCloseCallback: () => callback?.(),
      body: <QRCode value={bindingToken} />,
      width: 280
      });
    } catch (error) {
      console.error('Failed to fetch binding token data:', error);
    } finally {
      setLoading(false);
    }
  }, [callback, dispatch, identityId, openModal, handleVerifyPin]);

  const handleCallLastAPI = useCallback(async () => {
    const input = details.profiles?.[0]?.token ?? details.account?.email;
    
    if(isDirty){
      toast('error', messages({ id: 'FORM_DIRTY' }), { temporary: true });
      return false;
    }

    if(!input) {
      toast('error', messages({ id: 'MISSING_EMAIL' }), { temporary: true });
      return;
    }
    
    navigate('/logs', { state: { email: input } });
  }, [details.profiles, details.account?.email, isDirty, messages, navigate, toast]);

  const handleBindingToken = useCallback(async () => {
    const valid = await handleVerifyPin();
    if (!valid) return;

    try {
      if (profileId) {
        setLoadingBindingToken(true);
        const response = await dispatch(createBindCodes([profileId]));
        const bindingToken = response.data;
        
        openModal({
          body: <BindingTokenTable data={bindingToken} />,
          width: 500,
          bodySX: { height: 250 },
          actions: [
            { 
              label: messages({ id: 'CLOSE' }),
              onClick: () => {
                closeModal();
                callback?.();
              }
            }
          ]
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingBindingToken(false);
    }
  }, [dispatch, openModal, profileId, messages, closeModal, callback, handleVerifyPin]);
  if (!isShow) return null;

  return (
    <S.Card>
      <div>
        {!isStaff ? (
          <AgButton 
            width='100%' 
            label={messages({ id: 'VIEW_ID_CARD' })} 
            variant='outlined'
            disabled={isBound}
            onClick={isBound ? undefined : isIdOnly ? handleViewIDCard : handleCreateIDCard}
          />
        ) : (
          <AgButton 
            width='100%' 
            label={messages({ id: 'BINDING_TOKEN' })} 
            variant='outlined'
            disabled={isBound || isLoadBindingToken || isIdOnly}
            loading={isLoadBindingToken}
            onClick={isBound || isLoadBindingToken || isIdOnly ? undefined : handleBindingToken}
          />
        )}
        <AgButton 
          width='100%' 
          label={messages({ id: 'BINDING_QR_CODE' })} 
          variant='outlined'
          disabled={isBound || isLoading || isIdOnly}
          loading={isLoading}
          onClick={isBound || isLoading || isIdOnly ? undefined : handleShowQRCode}
        />
      </div>
      <Divider />

      <div>
        {access.hasSoftReset && (
          <AgButton 
            width='100%' 
            label={messages({ id: 'SOFT_RESET' })} 
            disabled={!(isBound || isIdOnly)}
            onClick={() => !(isBound || isIdOnly) ? undefined : handleUnbindModal(false)}
          />
        )}
        {access.hasHardReset && (
          <AgButton 
            width='100%' 
            label={messages({ id: 'HARD_RESET' })} 
            disabled={!(isBound || isIdOnly)}
            onClick={() => !(isBound || isIdOnly) ? undefined : handleUnbindModal(true)}
          />
        )}
      </div>

      {access.hasLogsUser && (
        <>
          <Divider />

          <AgButton 
            width='100%' 
            variant='outlined'
            label={messages({ id: 'SHOW_API_CALL' })} 
            onClick={handleCallLastAPI}
          />
        </>
      )}
    </S.Card>
  );
};

export default BindCard;
